import {
  Arrange,
  Button,
  Container,
  Split,
  SplitSection,
  SvgRedo,
  SvgSplit,
  SvgTrash,
  SvgUndo,
  Text,
  Tooltip,
} from '@loomhq/lens';
import React from 'react';

import { TRIM_SPLIT_PLAY_RANGE } from 'constants/hotkeys';
import { isMac } from 'utilities/device';
import { secondsToVideoTS } from 'utilities/time';
import styles from './styles.module.less';

const SmallButton = props => <Button size="small" {...props} />;

interface Props {
  currentTime: number;
  canSplitPlayRange: boolean;
  splitPlayRange: () => void;
  canDeletePlayRange: boolean;
  deletePlayRange: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  revertToOriginal: () => void;
  canRevertToOriginal: boolean;
}

const TooltipWithShortcut = ({ content, shortcut, children }) => (
  <Tooltip
    placement="bottomCenter"
    maxWidth="40"
    keepOpen={true}
    content={
      <Arrange gap="12px">
        <Text size="small" fontWeight="medium">
          {content}
        </Text>
        <Container
          paddingX="xsmall"
          paddingY="0"
          backgroundColor="rgba(255, 255, 255, 0.15)"
          style={{ borderRadius: '3px' }}
        >
          <Text
            size="small"
            color="rgba(244, 244, 250, 0.6)"
            fontWeight="medium"
          >
            {shortcut}
          </Text>
        </Container>
      </Arrange>
    }
  >
    {children}
  </Tooltip>
);

const TrimControlBar = ({
  currentTime,
  canSplitPlayRange,
  splitPlayRange,
  canDeletePlayRange,
  deletePlayRange,
  canUndo,
  canRedo,
  undo,
  redo,
  revertToOriginal,
  canRevertToOriginal,
}: Props): JSX.Element => {
  return (
    <Split gap="medium" justifyContent="space-between">
      <SplitSection>
        <Arrange gap="small">
          <TooltipWithShortcut
            content="Split to trim in the middle of a clip"
            shortcut={TRIM_SPLIT_PLAY_RANGE[0]}
          >
            <SmallButton
              isDisabled={!canSplitPlayRange}
              icon={<SvgSplit />}
              onClick={splitPlayRange}
            >
              Split{' '}
              {!isNaN(currentTime) && (
                <>
                  at{' '}
                  <span className={styles.timeDisplay}>
                    {secondsToVideoTS(Math.round(currentTime))}
                  </span>{' '}
                </>
              )}
            </SmallButton>
          </TooltipWithShortcut>
          <TooltipWithShortcut
            content="Remove the selected clip"
            shortcut="Del"
          >
            <SmallButton
              isDisabled={!canDeletePlayRange}
              icon={<SvgTrash />}
              onClick={deletePlayRange}
            >
              Delete
            </SmallButton>
          </TooltipWithShortcut>
        </Arrange>
      </SplitSection>
      <SplitSection>
        <Arrange gap="small">
          <SmallButton
            isDisabled={!canRevertToOriginal}
            onClick={revertToOriginal}
          >
            Revert to original
          </SmallButton>
          <TooltipWithShortcut
            content="Undo"
            shortcut={isMac ? 'Cmd-Z' : 'Ctrl-Z'}
          >
            <SmallButton
              isDisabled={!canUndo}
              icon={<SvgUndo />}
              onClick={undo}
            >
              Undo
            </SmallButton>
          </TooltipWithShortcut>
          <TooltipWithShortcut
            content="Redo"
            shortcut={isMac ? 'Cmd-Shift-Z' : 'Ctrl-Shift-Z'}
          >
            <SmallButton
              isDisabled={!canRedo}
              icon={<SvgRedo />}
              onClick={redo}
            >
              Redo
            </SmallButton>
          </TooltipWithShortcut>
        </Arrange>
      </SplitSection>
    </Split>
  );
};

export default TrimControlBar;
